import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Container, Grid, Typography, Box } from '@mui/material';
import './dashboard.scss';

export default function SettingsDashboard() {
  const navigate = useNavigate();
  return (
    <Container sx={{ p: 4, minHeight: '100vh' }}>
      <Button
        sx={{
          mb: 2,
          color: 'primary.main',
          '&:hover': { bgcolor: 'grey.200' },
        }}
        variant="text"
        onClick={() => {
          navigate('/help-center');
        }}
      >
        <span>← Back</span>
      </Button>
      <div className="help__center__container" lang="en">
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2}>
            <Grid item>
              <Button variant="outlined" href="/help-center/dashboard">
                Overview
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" href="/help-center/building">
                Building
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" href="/help-center/devices">
                Devices
              </Button>
            </Grid>
            <Grid item>
              <Button variant="contained" href="/help-center/settings">
                Settings
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" href="/help-center/map">
                Map
              </Button>
            </Grid>
          </Grid>
        </Box>
        <div className="help__center__hero">
          <h1>Settings Guide</h1>
        </div>

        <div className="content__box">
          <h2>General</h2>
          <h3>Modules</h3>
          <img src="/helpcenter/modules.png" alt="Modules" />
          <p>
            <strong>Modules:</strong> Here you can choose your module that will
            be visible in your dashboard.
          </p>
        </div>
        <div className="content__box">
          <h3>Building Automation</h3>
          <img src="/helpcenter/tenant.png" alt="Sastech Tenant" />
          <p>
            <strong>Sastech Tenant ID:</strong> Here you can place your Sastech
            Tenant ID,it must be in capital letters.<strong>Grid region</strong>{' '}
            will determine electricity prices.
          </p>
        </div>
        <div className="content__box">
          <h3>Public Display</h3>
          <img src="/helpcenter/publicDisplay.png" alt="Public Display" />
          <p>
            <strong>Public Display:</strong> Here is the link to your public
            display.
          </p>
        </div>

        <div className="content__box">
          <h3>Privacy</h3>
          <img src="/helpcenter/privacy.png" alt="Privacy" />
          <p>
            <strong>Privacy:</strong> This is where you setup the interval for
            anonymizing
          </p>
        </div>
        <div className="content__box">
          <h3>Azure Tenant ID</h3>
          <img src="/helpcenter/azure.png" alt="Azure Tenant" />
          <p>
            <strong>Azure Tenant ID:</strong> Here you can place your Azure
            Tenant ID.
          </p>
        </div>
        <div className="content__box">
          <h3>Vendor Deletion</h3>
          <img src="/helpcenter/deleteVendor.png" alt="Delete Vendor" />
          <p>
            <strong>Vendor Deletion:</strong> The system will delete the vendor
            only if the vendor is marked for deletion, the tenant status is set
            to <strong>deleted</strong>, and the vendor is{' '}
            <strong>inactive</strong>. The vendor will be deleted if the model
            was last updated more than 30 days ago, ensuring that the vendor is
            not in use.
          </p>
        </div>
        <div className="content__box">
          <h2>Add/Edit Device</h2>
          <h3>Active</h3>
          <img src="/helpcenter/active.png" alt="Active" />
          <p>
            <strong>Active:</strong> Choose if you want to have your device as
            active or inactive. Active devices are shown in devices overview.
          </p>

          <p>
            <strong>Display in Zone:</strong> Choose if you want to have your
            device displayed in Zone overview inside Building module.
          </p>
        </div>

        <div className="content__box">
          <h3>Type</h3>
          <img src="/helpcenter/type.png" alt="Type" />
          <p>
            <strong>Type:</strong> Choose a type.
          </p>
        </div>
        <div className="content__box">
          <h3>Category</h3>
          <img src="/helpcenter/category.png" alt="Category" />
          <p>
            <strong>Category:</strong> Choose a category from your created
            category list.
          </p>
        </div>

        <div className="content__box">
          <h2>Topics</h2>
          <img src="/helpcenter/topics.png" alt="Topics" />
          <p>
            <strong>Topics:</strong> Use this section to configure
            sensor-related data entries. You can add multiple topics, set the
            sensor tag, aggregation method, scaling options, number of decimals,
            and measurement units. Click <strong>+ Add Topic</strong> to insert
            new ones, or <strong>Remove</strong> to delete existing ones.
          </p>
        </div>
        <div className="content__box">
          <h2>Aggregator</h2>
          <img src="/helpcenter/aggregator.png" alt="Aggregator" />
          <p>
            <strong>Aggregator:</strong> Here you can choose your aggregation
            method. This setting determines how data from the sensor is
            processed over a period of time. The available options include:
          </p>
          <ul>
            <li>
              <strong>None:</strong> No aggregation is applied; raw data is used
              as-is.
            </li>
            <li>
              <strong>Sum:</strong> Adds all values over the aggregation period
              — ideal for totals like energy or volume.
            </li>
            <li>
              <strong>Average:</strong> Calculates the mean of all readings —
              useful for smoothing data like temperature.
            </li>
            <li>
              <strong>Max:</strong> Captures the highest value — good for
              monitoring peaks such as maximum load.
            </li>
            <li>
              <strong>Min:</strong> Captures the lowest value — useful for
              detecting minimum thresholds like voltage dips.
            </li>
            <li>
              <strong>Cumulative:</strong> Continuously sums values over time
              without resetting — ideal for tracking growing totals.
            </li>
            <li>
              <strong>Differential:</strong> Measures the change between
              successive readings — suitable for rate-of-change monitoring.
            </li>
            <li>
              <strong>Last:</strong> Uses the most recent value — helpful when
              only the latest status matters.
            </li>
          </ul>
          <p>
            Select the method that best fits how you intend to interpret or
            present the sensor data.
          </p>
        </div>
        <div className="content__box">
          <h3>EOS Device</h3>
          <img src="/helpcenter/power.png" alt="Power" />
          <p>
            <strong>Active:</strong> Choose if you want to have your EOS device
            as active or inactive. Active EOS devices are shown in devices
            overview.
          </p>
          <p>
            <strong>Producing Power:</strong> Choose if you want to have your
            EOS device is producing power or not.
          </p>
          <h3>Sensor Configuration</h3>
          <img src="/helpcenter/sensorConfig.png" alt="Configuration" />
          <p>
            <strong>Sensor Configuration:</strong> Choose if you want to have
            your EOS device as active or inactive. Active EOS devices are shown
            in devices overview.
          </p>
        </div>
        <div className="content__box">
          <h2>Categories</h2>
          <img src="/helpcenter/categoryType.png" alt="category Type" />
          <p>
            <strong>Category Type:</strong> Choose a category type.
          </p>
          <h3>Parent</h3>
          <img src="/helpcenter/parent.png" alt="Parent" />
          <p>
            <strong>Parent:</strong> Choose a parent for a category.
          </p>
          <h3>Public</h3>
          <img src="/helpcenter/public.png" alt="Public" />
          <p>
            <strong>Public:</strong> Choose if a category is public.
          </p>
        </div>
        <div className="content__box">
          <h3>Language</h3>
          <img src="/helpcenter/language.png" alt="Language" />
          <p>
            <strong>Language:</strong> Choose a Language for your dashboard.
          </p>
        </div>
        <div className="content__box">
          <h2>Filecloud</h2>
          <img src="/helpcenter/filecloud.png" alt="Filecloud" />
          <p>
            <strong>Filecloud:</strong> Securely store, organize and share files
            across your organization.
          </p>
        </div>
        <div className="content__box">
          <img src="/helpcenter/download.png" alt="Filecloud" />
          <p>
            <strong>Download:</strong> Download,delete or rename the file.
          </p>
        </div>
      </div>
    </Container>
  );
}
